import { commentLots, handleBlockLots, useLotDetails, useMultipleArticles } from '@gozoki/api';
import { Button, Chip, IconButton, Paper, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Lot } from '@gozoki/api-types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styles from '../../assets/styles/QualityPage.module.css';
import Page from '../../components/Page';
import { useToast } from '../../components/communication/Toast';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { formatDateOnly } from '../../utils/types/formatDates';
import { getArticleColumnDef } from './qualityUtils';

const LotStatus = ({ lot }: { lot: Lot }) => {
    let title = 'Non bloqué';
    if (lot.blocked) title = 'Bloqué';
    if (!lot.blocked && lot.blockedAt) title = 'Débloqué';
    return <Chip style={{ height: 35 }} label={title} color={lot.blocked ? 'error' : 'success'} />;
};

// Use shared column definitions from qualityUtils
const articlesColumnDef = getArticleColumnDef();

const LotPage = () => {
    const [Toast, showToast] = useToast();
    const queryClient = useQueryClient();
    const { lot: lotId } = useParams();

    const { data: lotDetails, isLoading: isLoadingLot } = useLotDetails(lotId);
    const [previousComment, setPreviousComment] = useState('');
    const [comment, setComment] = useState('');
    const [commentInitialized, setCommentInitialized] = useState(false);

    const navigate = useNavigate();

    const { data: articles, isLoading: isLoadingArticles } = useMultipleArticles(
        lotDetails?.articles
    );

    // Initialize comment when lot details are first loaded (only once)
    useEffect(() => {
        if (lotDetails && !commentInitialized) {
            setComment(lotDetails.comment || '');
            setPreviousComment(lotDetails.comment || '');
            setCommentInitialized(true);
        }
    }, [lotDetails, commentInitialized]);

    const commentHasChanged = useMemo(
        () => comment !== previousComment,
        [comment, previousComment]
    );

    // Memoize the comment change handler
    const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setComment(e.target.value);
    }, []);

    const { mutate: mutateBlockLot } = useMutation({
        mutationKey: ['blockLot', lotId],
        mutationFn: handleBlockLots,
        onSuccess: () => {
            const message = lotDetails?.blocked
                ? 'Lot débloqué avec succès'
                : 'Lot bloqué avec succès';
            showToast({ message, severity: 'success' });
            // Invalidate lot details to update the status
            queryClient.invalidateQueries({ queryKey: ['quality', 'lotDetails', lotId] });
            // Invalidate lots list on Quality page so it refetches with updated data
            queryClient.invalidateQueries({ queryKey: ['quality', 'lotsData'] });
        },
        onError: (error) => {
            showToast({ message: error.message || 'Erreur', severity: 'error' });
        },
    });

    const { mutate: mutateCommentLot } = useMutation({
        mutationKey: ['commentLot', lotId],
        mutationFn: commentLots,
        onSuccess: () => {
            showToast({ message: 'Commentaire modifié avec succès', severity: 'success' });
            setPreviousComment(comment);
            // Invalidate lot details to update the comment
            queryClient.invalidateQueries({ queryKey: ['quality', 'lotDetails', lotId] });
            // Invalidate lots list on Quality page so it refetches with updated data
            queryClient.invalidateQueries({ queryKey: ['quality', 'lotsData'] });
        },
        onError: (error) => {
            showToast({ message: error.message || 'Erreur', severity: 'error' });
        },
    });
    // This silent function is used when blocking a lot to avoid getting 2 toasts
    const { mutate: mutateCommentLotSilent } = useMutation({
        mutationKey: ['commentLotSilent', lotId],
        mutationFn: commentLots,
        onSuccess: () => {
            setPreviousComment(comment);
            // Don't invalidate here - the block mutation will handle it
        },
        onError: (error) => {
            showToast({ message: error.message || 'Erreur', severity: 'error' });
        },
    });

    // Memoize header info array to avoid recreating on every render
    const headerInfo = useMemo(() => {
        if (!lotDetails) return [];
        return [
            { title: 'N° de lot', value: lotDetails.lot },
            { title: 'EAN 13', value: lotDetails.ean },
            { title: 'Libellé', value: lotDetails.label },
            { title: 'Origine', value: lotDetails.tracking },
            {
                title: 'DLC',
                value: formatDateOnly(lotDetails.dlc),
            },
            { title: "Nb d'articles", value: lotDetails.nb },
        ];
    }, [lotDetails]);

    // Memoize articles rows to avoid recreating on every render
    const articleRows = useMemo(() => {
        if (!articles) return [];
        return articles.map((a) => ({
            id: a.checkpointArticleRef,
            checkPointArticleRef: a.checkpointArticleRef,
            storeLabel: a.store.label,
            warehouseAt: a.warehouseAt,
            shippingAt: a.shippingAt,
            boutiqueAt: a.boutiqueAt,
            soldAt: a.state === 'SOLD' ? a.lostAt : null,
            stolenAt: a.state === 'STOLEN' ? a.lostAt : null,
            destroyedAt: a.state === 'DESTROYED' ? a.lostAt : null,
        }));
    }, [articles]);

    if (isLoadingLot) {
        return (
            <Page>
                <Toast />
                <div style={{ display: 'flex', paddingLeft: 10, alignItems: 'center', gap: 8 }}>
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon
                            sx={{
                                color: ThemeColors.BLACK,
                                height: '24px',
                                width: '24px',
                            }}
                        />
                    </IconButton>
                    <Typography style={fonts.pageTitle} flexGrow={1}>
                        Détails du lot
                    </Typography>
                </div>
                <Typography style={{ padding: 20 }}>Chargement...</Typography>
            </Page>
        );
    }

    if (!lotDetails) {
        return (
            <Page>
                <Toast />
                <div style={{ display: 'flex', paddingLeft: 10, alignItems: 'center', gap: 8 }}>
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon
                            sx={{
                                color: ThemeColors.BLACK,
                                height: '24px',
                                width: '24px',
                            }}
                        />
                    </IconButton>
                    <Typography style={fonts.pageTitle} flexGrow={1}>
                        Détails du lot
                    </Typography>
                </div>
                <Typography style={{ padding: 20 }}>Lot non trouvé</Typography>
            </Page>
        );
    }

    return (
        <Page>
            <Toast />
            <div style={{ display: 'flex', paddingLeft: 10, alignItems: 'center', gap: 8 }}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={fonts.pageTitle} flexGrow={1}>
                    Détails du lot
                </Typography>
            </div>

            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                <div className={styles.headerPaper}>
                    {headerInfo.map((item, index) => (
                        <div key={index} style={{ flex: 1 }}>
                            <p style={fonts.inputSubtitle}>{item.title}</p>
                            <div style={{ padding: 6 }} />
                            <p style={fonts.inputHeader}>{item.value}</p>
                        </div>
                    ))}
                </div>
            </Paper>

            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                <div className={styles.contentPaper}>
                    <div className={styles.detailsLine}>
                        <div style={{ flex: 1 }} className={styles.detailsLinePart}>
                            <p style={fonts.inputHeader}>Statut</p>
                        </div>
                        <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
                            <LotStatus lot={lotDetails} />
                        </div>
                    </div>
                    <div className={styles.detailsLine}>
                        <div style={{ flex: 1 }} className={styles.detailsLinePart}>
                            <p style={fonts.inputHeader}>Commentaire</p>
                        </div>
                        <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
                            <TextField
                                variant="outlined"
                                placeholder="Commentaire"
                                fullWidth
                                value={comment}
                                onChange={handleCommentChange}
                            />
                        </div>
                    </div>
                    <div className={styles.detailsLine}>
                        <div style={{ flex: 1 }} className={styles.detailsLinePart}>
                            <p style={fonts.inputHeader}>Bloqué à</p>
                        </div>
                        <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
                            {lotDetails.blockedAt ? (
                                <p>
                                    {new Date(lotDetails.blockedAt).toLocaleDateString('fr-FR')}
                                    {' - '}
                                    {new Date(lotDetails.blockedAt).toLocaleTimeString('fr-FR')}
                                </p>
                            ) : (
                                <p>-</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.detailsLine}>
                        <div style={{ flex: 1 }} className={styles.detailsLinePart}>
                            <p style={fonts.inputHeader}>Débloqué à</p>
                        </div>
                        <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
                            {lotDetails.unBlockedAt ? (
                                <p>
                                    {new Date(lotDetails.unBlockedAt).toLocaleDateString('fr-FR')}
                                    {' - '}
                                    {new Date(lotDetails.unBlockedAt).toLocaleTimeString('fr-FR')}
                                </p>
                            ) : (
                                <p>-</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.detailsLine}>
                        <div style={{ flex: 1 }} className={styles.detailsLinePart}>
                            <p style={fonts.inputHeader}>Bloqué par</p>
                        </div>
                        <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
                            {lotDetails.blockedBy ? <p>{lotDetails.blockedBy}</p> : <p>-</p>}
                        </div>
                    </div>
                </div>
                <div className={styles.headerPaper} style={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        color="info"
                        style={{ margin: 10 }}
                        disabled={!commentHasChanged}
                        onClick={() => {
                            // Require comment when batch is blocked
                            if (lotDetails.blocked && !comment.trim()) {
                                showToast({
                                    message:
                                        'Un lot bloqué doit obligatoirement avoir un commentaire',
                                    severity: 'error',
                                });
                                return;
                            }
                            mutateCommentLot({ ids: lotDetails.articles, comment });
                        }}
                    >
                        Modifier le commentaire
                    </Button>
                    <Button
                        variant="contained"
                        color={lotDetails.blocked ? 'success' : 'error'}
                        onClick={() => {
                            // Require comment when blocking
                            if (!lotDetails.blocked && !comment.trim()) {
                                showToast({
                                    message:
                                        'Veuillez ajouter un commentaire avant de bloquer le lot',
                                    severity: 'error',
                                });
                                return;
                            }
                            // Save comment if it has changed (use silent mutation)
                            if (commentHasChanged) {
                                mutateCommentLotSilent({ ids: lotDetails.articles, comment });
                            }
                            mutateBlockLot(lotDetails.articles);
                        }}
                        style={{ margin: 10 }}
                    >
                        {lotDetails.blocked ? 'Débloquer' : 'Bloquer'}
                    </Button>
                </div>
            </Paper>

            {articleRows.length > 0 ? (
                <Paper
                    sx={{
                        marginTop: '16px',
                    }}
                >
                    <SimpleDataGrid
                        columns={articlesColumnDef}
                        rows={articleRows}
                        loading={isLoadingArticles}
                        disableRowSelectionOnClick
                        checkboxSelection={false}
                    />
                </Paper>
            ) : null}
        </Page>
    );
};

export default LotPage;
